import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';

const GDPR = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#0a0a2e] text-white py-24">
      <div className="max-w-4xl mx-auto px-6">
        <Button
          onClick={() => navigate('/')}
          variant="outline"
          className="mb-8 border-cyan-500/50 hover:border-cyan-400 bg-transparent text-white"
        >
          <ArrowLeft className="mr-2" size={20} />
          Back to Home
        </Button>

        <h1 className="text-4xl lg:text-5xl font-bold mb-4 bg-gradient-to-r from-cyan-400 to-pink-500 bg-clip-text text-transparent">
          GDPR Compliance
        </h1>
        <p className="text-gray-400 mb-8">General Data Protection Regulation</p>

        <div className="space-y-8 text-gray-300">
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">1. Our Commitment to GDPR</h2>
            <p className="leading-relaxed">
              StreamShares is committed to complying with the General Data Protection Regulation (GDPR) and protecting the privacy rights of individuals in the European Economic Area (EEA) and the United Kingdom.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">2. Your Rights Under GDPR</h2>
            <p className="leading-relaxed mb-4">
              Under GDPR, you have the following rights regarding your personal data:
            </p>

            <div className="space-y-4">
              <div className="p-5 bg-gray-900/50 rounded-lg border border-cyan-500/20">
                <h3 className="text-lg font-semibold text-cyan-400 mb-2">Right to Access</h3>
                <p>You have the right to request copies of your personal data.</p>
              </div>

              <div className="p-5 bg-gray-900/50 rounded-lg border border-purple-500/20">
                <h3 className="text-lg font-semibold text-purple-400 mb-2">Right to Rectification</h3>
                <p>You have the right to request correction of inaccurate or incomplete personal data.</p>
              </div>

              <div className="p-5 bg-gray-900/50 rounded-lg border border-pink-500/20">
                <h3 className="text-lg font-semibold text-pink-400 mb-2">Right to Erasure (Right to be Forgotten)</h3>
                <p>You have the right to request deletion of your personal data under certain circumstances.</p>
              </div>

              <div className="p-5 bg-gray-900/50 rounded-lg border border-blue-500/20">
                <h3 className="text-lg font-semibold text-blue-400 mb-2">Right to Restrict Processing</h3>
                <p>You have the right to request restriction of processing your personal data.</p>
              </div>

              <div className="p-5 bg-gray-900/50 rounded-lg border border-green-500/20">
                <h3 className="text-lg font-semibold text-green-400 mb-2">Right to Data Portability</h3>
                <p>You have the right to request transfer of your data to another organization or directly to you.</p>
              </div>

              <div className="p-5 bg-gray-900/50 rounded-lg border border-orange-500/20">
                <h3 className="text-lg font-semibold text-orange-400 mb-2">Right to Object</h3>
                <p>You have the right to object to processing of your personal data for certain purposes.</p>
              </div>

              <div className="p-5 bg-gray-900/50 rounded-lg border border-red-500/20">
                <h3 className="text-lg font-semibold text-red-400 mb-2">Rights Related to Automated Decision Making</h3>
                <p>You have the right not to be subject to decisions based solely on automated processing.</p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">3. Legal Basis for Processing</h2>
            <p className="leading-relaxed mb-4">
              We process your personal data based on the following legal grounds:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong>Consent:</strong> You have given clear consent for us to process your personal data for a specific purpose</li>
              <li><strong>Contract:</strong> Processing is necessary for a contract we have with you, or because you have asked us to take specific steps before entering into a contract</li>
              <li><strong>Legal Obligation:</strong> Processing is necessary for us to comply with the law</li>
              <li><strong>Legitimate Interests:</strong> Processing is necessary for our legitimate interests or the legitimate interests of a third party</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">4. How to Exercise Your Rights</h2>
            <p className="leading-relaxed mb-4">
              To exercise any of your GDPR rights, please:
            </p>
            <ol className="list-decimal list-inside space-y-2 ml-4">
              <li>Send an email to info@streamshares.co.uk with the subject "GDPR Request"</li>
              <li>Clearly state which right(s) you wish to exercise</li>
              <li>Provide sufficient information to verify your identity</li>
              <li>Specify any relevant details (e.g., specific data you want to access)</li>
            </ol>
            <p className="mt-4 leading-relaxed">
              We will respond to your request within one month, though this period may be extended by two further months for complex requests.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">5. Data Protection Officer</h2>
            <p className="leading-relaxed mb-4">
              We have appointed a Data Protection Officer (DPO) who is responsible for overseeing our data protection strategy and GDPR compliance.
            </p>
            <div className="p-6 bg-gray-900/50 rounded-lg border border-gray-700/50">
              <p className="text-cyan-400 font-semibold mb-2">Contact our DPO:</p>
              <p>Email: dpo@streamshares.co.uk</p>
              <p>Address: StreamShares, London, United Kingdom</p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">6. Data Retention</h2>
            <p className="leading-relaxed mb-4">
              We retain your personal data only for as long as necessary for the purposes set out in our Privacy Policy:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong>Account Data:</strong> Retained while your account is active, plus 30 days after deletion request</li>
              <li><strong>Financial Records:</strong> Retained for 7 years to comply with legal obligations</li>
              <li><strong>Marketing Data:</strong> Retained until you withdraw consent or 2 years of inactivity</li>
              <li><strong>Usage Data:</strong> Aggregated and anonymized data may be retained indefinitely</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">7. International Data Transfers</h2>
            <p className="leading-relaxed mb-4">
              When transferring data outside the EEA/UK, we ensure appropriate safeguards are in place:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Standard Contractual Clauses approved by the European Commission</li>
              <li>Adequacy decisions by the European Commission</li>
              <li>Binding Corporate Rules where applicable</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">8. Data Breach Notification</h2>
            <p className="leading-relaxed">
              In the event of a data breach that is likely to result in a risk to your rights and freedoms, we will notify you and the relevant supervisory authority within 72 hours of becoming aware of the breach.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">9. Complaints</h2>
            <p className="leading-relaxed mb-4">
              If you believe we have not complied with GDPR, you have the right to lodge a complaint with your local supervisory authority:
            </p>
            <div className="p-6 bg-gray-900/50 rounded-lg border border-gray-700/50">
              <p className="font-semibold mb-2">UK Supervisory Authority:</p>
              <p>Information Commissioner's Office (ICO)</p>
              <p>Website: <a href="https://ico.org.uk" className="text-cyan-400 hover:underline">ico.org.uk</a></p>
              <p>Helpline: 0303 123 1113</p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">10. Updates to GDPR Compliance</h2>
            <p className="leading-relaxed">
              We regularly review and update our GDPR compliance procedures. Any material changes will be communicated through our Privacy Policy and on this page.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">11. Contact Information</h2>
            <div className="p-6 bg-gradient-to-r from-cyan-500/10 to-pink-500/10 rounded-lg border border-cyan-500/30">
              <p className="text-white font-semibold mb-4">For GDPR-related queries:</p>
              <p>Email: info@streamshares.co.uk</p>
              <p>DPO Email: dpo@streamshares.co.uk</p>
              <p>Location: London, United Kingdom</p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default GDPR;
